import { useLayoutEffect, useMemo, useRef } from 'react';
import { Platform, TextInput, TextStyle } from 'react-native';
import {
  INPUT_MAX_HEIGHT,
  INPUT_PADDING_VERTICAL,
  getInputHeightForLines,
  linesFromScrollHeight,
} from './utils';

const isWeb = Platform.OS === 'web';

/**
 * The <textarea> react-native-web renders for a multiline TextInput. Only the
 * three properties we touch are declared, so this still compiles - and stays
 * inert - on native, where the ref is a TextInput instance.
 */
type TextAreaNode = {
  scrollHeight: number;
  scrollTop: number;
  style: { height: string };
};

const isTextAreaNode = (node: unknown): node is TextAreaNode => {
  const candidate = node as TextAreaNode | null;
  return (
    !!candidate &&
    typeof candidate.scrollHeight === 'number' &&
    !!candidate.style
  );
};

/**
 * scrollHeight is max(contentHeight, clientHeight), so it can only be trusted
 * while the box is shorter than its content: with a height pinned on the
 * textarea it keeps reporting the old, larger value and the composer never
 * shrinks back. Collapse to 0, read, restore - all synchronously, so the
 * browser never paints the collapsed box. scrollTop is saved because
 * collapsing clamps it to 0.
 *
 * Touch nothing here but height / scrollHeight / scrollTop: reading or writing
 * value, selectionStart or focus would break IME composition (Japanese input).
 */
const measureWebLines = (node: unknown): number | null => {
  if (!isTextAreaNode(node)) {
    return null;
  }
  const previousHeight = node.style.height;
  const previousScrollTop = node.scrollTop;
  node.style.height = '0px';
  const { scrollHeight } = node;
  node.style.height = previousHeight;
  node.scrollTop = previousScrollTop;
  // 0 while the element is not rendered (display: none) - keep what we had.
  if (scrollHeight <= 2 * INPUT_PADDING_VERTICAL) {
    return null;
  }
  return linesFromScrollHeight(scrollHeight);
};

/**
 * MIK-4818. A <textarea> never grows on its own (react-native-web passes no
 * `rows`, so it sits at the UA default of 2 rows), so on web we measure the
 * content and pin a height. Native already auto-sizes between its own min and
 * maxHeight and is intentionally left untouched.
 */
export const useAutoGrowInput = ({
  value,
  lines,
  width,
  onLinesChange,
}: {
  value: string;
  lines: number;
  width?: number;
  onLinesChange: (lines: number) => void;
}) => {
  const inputRef = useRef<TextInput>(null);

  // Keyed on `value` (typing, paste, IME commit, and the reset inside
  // sendMessage) and on `width`, which decides where lines wrap.
  // useLayoutEffect so the resulting setState is flushed before paint.
  useLayoutEffect(() => {
    if (!isWeb) {
      return;
    }
    const measured = measureWebLines(inputRef.current);
    if (measured != null) {
      onLinesChange(measured);
    }
  }, [value, width, onLinesChange]);

  // maxHeight is raised here rather than in the shared stylesheet so native
  // keeps its current 120 cap and stays byte-identical. On web maxHeight would
  // otherwise clamp our height and cap the composer at 4 lines.
  const heightStyle = useMemo<TextStyle | undefined>(
    () =>
      isWeb
        ? { height: getInputHeightForLines(lines), maxHeight: INPUT_MAX_HEIGHT }
        : undefined,
    [lines]
  );

  return { inputRef, heightStyle };
};
